// Hooks da REGRA DE FILA do discador (queue_config), que é global por org.
// Mesmo padrão de use-calls.ts / use-leads.ts:
//   - `src/api/queue-config.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//
// Arquivo separado de use-dialer.ts pelo mesmo motivo que os módulos de api
// são dois: esta é a configuração da ORGANIZAÇÃO, não o ato de discar. A chave
// ["queue-config"] é uma raiz de cache só dela — nada do discador a invalida, e
// ela não invalida nada do discador.
import { useMutation, useQuery, useQueryClient } from "@organizacaox/lai-design-system/query";
import {
  updateNamedQueueOperation,
  activateNamedDialerQueue,
  deleteNamedDialerQueue,
  saveNamedDialerQueue,
  type NamedDialerQueueInput,
  getQueueConfig,
  updateQueueConfig,
  type QueueConfig,
} from "@/api/queue-config";

// Uma chave só, sem parâmetro: a config é uma por organização. Três telas
// (regra de fila, campanhas e o quadro da fila) dividem esta MESMA entrada de
// cache de propósito — quem já abriu uma reaproveita nas outras.
export const queueConfigKeys = {
  all: ["queue-config"] as const,
};

// A jornada de primeiro acesso tem o passo "defina a regra da fila", então
// salvar aqui derruba também o cartão da jornada e o contador da sidebar. É de
// outro domínio (ainda não extraído), por isso a chave fica literal e na mesma
// ordem de antes; quando a jornada sair, esta linha vai junto.
const JOURNEY_QUERY_KEY = ["onboarding", "journey"] as const;

// `enabled` existe porque a tela de campanhas só lê a config para o GESTOR (ela
// alimenta os placeholders de "herdado da fila"); o vendedor não a pede.
export function useQueueConfig(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queueConfigKeys.all,
    queryFn: () => getQueueConfig(),
    enabled: options.enabled,
  });
}

// O corpo aceito pelo PATCH — a tela monta o dela e o hook não opina sobre
// quais campos vão junto.
export type QueueConfigPatch = Parameters<typeof updateQueueConfig>[0];

// PATCH parcial: a tela de regra de fila salva a distribuição por vendedor e a
// cadência por desfecho em DOIS botões separados, cada um mandando só o seu
// pedaço — daí o patch entrar como variável da mutação, e não um hook por
// botão. Os `handlers` entram como parâmetro (e não via
// `mutate(vars, { onSuccess })`) para a ORDEM original ser preservada: a
// invalidação primeiro, o toast depois, como estava nas duas mutações.
//
// O `mutationFn` chama a função de api dentro de uma seta: o TanStack invoca o
// mutationFn com (variáveis, contexto), e uma função que aceitasse um segundo
// parâmetro receberia o contexto da mutação como se fosse dado.
export function useUpdateQueueConfig(
  handlers: { onSuccess?: (config: QueueConfig) => void; onError?: (error: Error) => void } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: QueueConfigPatch) => updateQueueConfig(patch),
    onSuccess: (config) => {
      void queryClient.invalidateQueries({ queryKey: queueConfigKeys.all });
      void queryClient.invalidateQueries({ queryKey: JOURNEY_QUERY_KEY });
      handlers.onSuccess?.(config);
    },
    onError: handlers.onError,
  });
}

export function useNamedDialerQueueActions() {
  const client = useQueryClient();
  const onSuccess = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queueConfigKeys.all }),
      ...["dialer-queue", "queue-kanban", "queue-kanban-totals", "queue-report"].map((key) =>
        client.invalidateQueries({ queryKey: [key] }),
      ),
      client.invalidateQueries({ queryKey: ["reports", "queue"] }),
    ]);
  };
  const save = useMutation({
    mutationFn: ({ id, ...input }: NamedDialerQueueInput & { id?: string }) =>
      saveNamedDialerQueue(input, id),
    onSuccess,
  });
  const activate = useMutation({
    mutationFn: (id: string | null) => activateNamedDialerQueue(id),
    onSuccess,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteNamedDialerQueue(id),
    onSuccess,
  });
  return { save, activate, remove };
}

export function useUpdateQueueOperation(
  queueId: string | undefined,
  handlers: { onSuccess?: (config: QueueConfig) => void; onError?: (error: Error) => void } = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (patch: Parameters<typeof updateNamedQueueOperation>[1]) =>
      updateNamedQueueOperation(queueId ?? "general", patch),
    onSuccess: async (config) => {
      await Promise.all(
        [
          queueConfigKeys.all,
          ["dialer-queue"],
          ["queue-kanban"],
          ["queue-kanban-totals"],
          ["queue-report"],
          ["reports", "queue"],
        ].map((queryKey) => client.invalidateQueries({ queryKey })),
      );
      handlers.onSuccess?.(config);
    },
    onError: handlers.onError,
  });
}
